<div class="survey-container" [formGroup]="answersForm">
  <ng-template #measureLabel let-question>
    @if (measureCodeLabel(question); as codeLabel) {
      @if (question.hyperlink) {
        <a
          class="survey-measure-link"
          [href]="question.hyperlink"
          target="_blank"
          rel="noopener noreferrer"
          (click)="$event.stopPropagation()"
        >{{ codeLabel }}</a>: {{ question.name }}
      } @else {
        {{ codeLabel }}: {{ question.name }}
      }
    } @else {
      {{ question.name }}
    }
  </ng-template>

  <div class="survey-header">
    <div>
      <h2 class="survey-title">Quality Measure Selection Survey</h2>
      <p class="survey-description">
        This survey helps identify your organization's clinical specialties, patient mix, and operational
        capabilities.
      </p>
    </div>
    @if (isSuperAdmin$ | async) {
      <button
        mat-raised-button
        color="primary"
        class="export-button"
        matTooltip="Export the current survey answers as a PDF"
        [disabled]="isSaving() || isExporting()"
        (click)="onExport()"
      >
        <mat-icon>download</mat-icon>
        Export
      </button>
    }
  </div>

  @if (isLoading()) {
    <p class="survey-loading">Loading survey...</p>
  }

  @if (!isLoading()) {
    <div class="survey-collapse-toolbar">
      <button type="button" mat-button (click)="expandAllSubSections()">
        <mat-icon>unfold_more</mat-icon>
        Expand All
      </button>
      <button type="button" mat-button (click)="collapseAllSubSections()">
        <mat-icon>unfold_less</mat-icon>
        Collapse All
      </button>
    </div>
  }

  <mat-accordion multi="true" class="survey-sections" displayMode="flat">
  @for (section of sections; track section.id) {
    <mat-expansion-panel class="survey-section" [expanded]="true">
      <mat-expansion-panel-header class="survey-section-header">
        <mat-panel-title class="survey-section-heading">{{ section.name }}</mat-panel-title>
      </mat-expansion-panel-header>

      @for (subSection of section.subSections; track subSection.id) {
        <div class="survey-subsection">
          <button
            type="button"
            class="survey-subsection-toggle"
            (click)="toggleSubSection(section.id, subSection.id)"
          >
            <mat-icon>{{ isSubSectionCollapsed(section.id, subSection.id) ? "chevron_right" : "expand_more" }}</mat-icon>
            <h4 class="survey-subsection-heading">{{ subSection.name }}</h4>
          </button>

          @if (!isSubSectionCollapsed(section.id, subSection.id)) {
            @for (item of subSection.items; track item.kind === 'radioGroup' ? item.radioGroupId : item.question.id) {
            @switch (item.kind) {
              @case ("radioGroup") {
                <div class="survey-question survey-radio-group">
                  <mat-radio-group [formControlName]="radioGroupControlKey(item.radioGroupId)">
                    @for (question of item.questions; track question.id) {
                      <mat-radio-button [value]="question.id">
                        <ng-container *ngTemplateOutlet="measureLabel; context: { $implicit: question }"></ng-container>
                      </mat-radio-button>
                    }
                  </mat-radio-group>
                </div>
              }
              @case ("single") {
                @switch (item.question.questionType) {
                  @case ("Relevance") {
                    <div class="survey-question survey-relevance">
                      <span class="survey-relevance-label">
                        <ng-container *ngTemplateOutlet="measureLabel; context: { $implicit: item.question }"></ng-container>
                      </span>
                      <div class="survey-relevance-row">
                        <span class="survey-relevance-group-label">Relevance:</span>
                        <mat-radio-group
                          class="survey-relevance-options"
                          [formControlName]="questionControlKey(item.question.id)"
                          (change)="onAnswerChange(item.question)"
                        >
                          @for (option of relevanceOptions; track option) {
                            <mat-radio-button [value]="option">{{ option }}</mat-radio-button>
                          }
                        </mat-radio-group>
                      </div>
                    </div>
                  }
                  @case ("Checkbox") {
                    <div class="survey-question">
                      <mat-checkbox
                        [formControlName]="questionControlKey(item.question.id)"
                        (change)="onAnswerChange(item.question)"
                      >
                        <ng-container *ngTemplateOutlet="measureLabel; context: { $implicit: item.question }"></ng-container>
                      </mat-checkbox>
                    </div>
                  }
                  @case ("Radio") {
                    <div class="survey-question survey-relevance">
                      <span class="survey-relevance-label">
                        <ng-container *ngTemplateOutlet="measureLabel; context: { $implicit: item.question }"></ng-container>
                      </span>
                      <div class="survey-relevance-row">
                        <mat-radio-group
                          class="survey-relevance-options"
                          [formControlName]="questionControlKey(item.question.id)"
                          (change)="onAnswerChange(item.question)"
                        >
                          @for (option of item.question.options ?? []; track option) {
                            <mat-radio-button [value]="option">{{ option }}</mat-radio-button>
                          }
                        </mat-radio-group>
                      </div>
                    </div>
                  }
                  @case ("TextArea") {
                    <div class="survey-question survey-textarea">
                      <mat-form-field appearance="outline" class="survey-full-width">
                        <mat-label>{{ questionDisplayLabel(item.question) }}</mat-label>
                        <textarea
                          matInput
                          rows="4"
                          [formControlName]="questionControlKey(item.question.id)"
                          [maxlength]="item.question.maxLength"
                        ></textarea>
                      </mat-form-field>
                      @if (item.question.maxLength) {
                        <div class="survey-char-count">
                          {{ getCharacterCount(item.question) }}/{{ item.question.maxLength }}
                        </div>
                      }
                    </div>
                  }
                  @case ("Dropdown") {
                    <div class="survey-question">
                      <mat-form-field appearance="outline" class="survey-full-width">
                        <mat-label>{{ questionDisplayLabel(item.question) }}</mat-label>
                        <mat-select [formControlName]="questionControlKey(item.question.id)">
                          @for (option of item.question.options ?? []; track option) {
                            <mat-option [value]="option">{{ option }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    </div>
                  }
                  @case ("Date") {
                    <div class="survey-question">
                      <mat-form-field appearance="outline" class="survey-full-width">
                        <mat-label>{{ questionDisplayLabel(item.question) }}</mat-label>
                        <input
                          matInput
                          [matDatepicker]="datePicker"
                          [formControlName]="questionControlKey(item.question.id)"
                        />
                        <mat-datepicker-toggle matIconSuffix [for]="datePicker"></mat-datepicker-toggle>
                        <mat-datepicker #datePicker></mat-datepicker>
                      </mat-form-field>
                    </div>
                  }
                  @case ("DateTime") {
                    <div class="survey-question">
                      <mat-form-field appearance="outline" class="survey-full-width">
                        <mat-label>{{ questionDisplayLabel(item.question) }}</mat-label>
                        <input
                          matInput
                          type="datetime-local"
                          [formControlName]="questionControlKey(item.question.id)"
                        />
                      </mat-form-field>
                    </div>
                  }
                }
              }
            }
            }
          }
        </div>
      }
    </mat-expansion-panel>
  }
  </mat-accordion>
</div>
